import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Discord channel types we care about (see discord-api-types).
 * We only expose the ones a dashboard config usually wants.
 */
export const CHANNEL_TYPE = {
  GUILD_TEXT: 0,
  GUILD_VOICE: 2,
  GUILD_CATEGORY: 4,
  GUILD_ANNOUNCEMENT: 5,
  GUILD_FORUM: 15,
} as const;

interface Channel {
  id: string;
  name: string;
  type: number;
  parent_id: string | null;
  position: number;
}

interface Props {
  guildId: string;
  value: string | null;
  onChange: (channelId: string | null) => void;
  /** Filter to only these channel types (from CHANNEL_TYPE). Default: text channels. */
  types?: number[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Native <select> populated from the guild's channels (fetched via
 * GET /api/guilds/:id/channels). Categories are shown as prefixes on their
 * children so users can visually parse hierarchy.
 */
export function ChannelPicker({
  guildId,
  value,
  onChange,
  types = [CHANNEL_TYPE.GUILD_TEXT],
  placeholder = '— Channel wählen —',
  disabled,
  className,
}: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['guild-channels', guildId],
    queryFn: () => api<{ channels: Channel[] }>(`/api/guilds/${guildId}/channels`),
    enabled: !!guildId,
    staleTime: 30_000,
  });

  const channels = (data?.channels ?? [])
    .filter((c) => types.includes(c.type))
    .sort((a, b) => a.position - b.position);

  const categories = new Map(
    (data?.channels ?? [])
      .filter((c) => c.type === CHANNEL_TYPE.GUILD_CATEGORY)
      .map((c) => [c.id, c.name])
  );

  return (
    <div>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || isLoading || !!error}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          className
        )}
      >
        <option value="">
          {isLoading ? 'Lade Channels…' : error ? 'Fehler beim Laden' : placeholder}
        </option>
        {channels.map((c) => (
          <option key={c.id} value={c.id}>
            {c.parent_id ? `${categories.get(c.parent_id) ?? '?'} / ` : ''}
            {c.name}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-xs text-destructive">{(error as Error).message}</p>
      )}
    </div>
  );
}
